import React, { useCallback, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Flex, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormFeedback,
  IButton,
  Input,
  Select,
  useToast
} from '@morressier/alembic';
import { useCreateSubmissionNote } from 'src/api/commands';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { SELECTED_ITEM_QUERY_KEY } from 'src/constants';
import { searchJournals, searchUsers } from 'src/api/queries';
import ProtectedActionButton from 'src/components/ProtectedAction/Button';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { debounce } from 'lodash';
import TaggingEditor from '../NotesTab/MentionsPlugin';
import { FieldLabel } from 'src/components/Field';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import {
  INoteCategory,
  ISubmissionFile,
  ITargetTags,
  TaggableEntity
} from '@publishing-engine/domain/models';
import { notesContentSchema } from '@publishing-engine/domain/validation';
import { toSentenceCasePreserveAcronyms } from 'src/utils/string';

type INoteFormPayload = {
  title: string;
  note: string;
  mentions: TaggableEntity[];
  attachments?: ISubmissionFile[];
  category: INoteCategory;
};

interface IProps {
  onAddNote?: () => void;
  targetTag?: ITargetTags;
  actionButtonProps?: Partial<IButton>;
}

const AddNoteForm: React.FC<IProps> = ({ onAddNote, actionButtonProps }) => {
  const {
    fieldsResponse: { organizationId }
  } = useSubmissionData();

  const method = useForm<INoteFormPayload>({
    resolver: zodResolver(notesContentSchema),
    mode: 'onSubmit',
    defaultValues: {
      title: '',
      note: '',
      category: 'General',
      mentions: [],
      attachments: []
    }
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    watch,
    reset
  } = method;

  const toast = useToast();
  const { submissionId: paramId = '' } = useParams();
  const ctxId = useCurrentContextId() ?? '';
  const searchParams = new URLSearchParams(location.search);
  const selectedItem = searchParams.get(SELECTED_ITEM_QUERY_KEY);
  const submissionId = selectedItem || paramId;
  const [userQuery, setUserQuery] = useState<string>('');
  const [journalQuery, setJournalQuery] = useState<string>('');

  // This is used to force a re-render of the editor when the note is updated
  const [editorKey, setEditorKey] = useState(0);
  // This is used to force the entire form to rerender on submit
  const [formKey, setFormKey] = useState(0);
  const isResettingRef = useRef(false);

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-search', userQuery, { organizationId }],
    queryFn: () => searchUsers(userQuery, { organizationId })
  });

  const { data: journals, isLoading: isLoadingJournals } = useQuery({
    queryKey: ['journals-search', journalQuery, { organizationId }],
    queryFn: () => searchJournals(journalQuery, { organizationId })
  });

  const mappedUsers = users?.collection.map(user => ({
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    type: 'user' as const
  }));

  const mappedJournals = journals?.collection.map(journal => ({
    id: journal.id,
    name: journal.data.name,
    shortname: journal.data.shortname,
    email: journal.config.email ?? '',
    type: 'journal' as const,
    focusIssues: journal.data.focusIssues,
    description: journal.data.description,
    organizationId: journal.organizationId,
    introduction: journal.data.introduction
  }));

  const createNoteMutation = useCreateSubmissionNote({
    queryKey: ['submission-notes', { submissionId }],
    submissionId,
    submissionContextId: ctxId,
    organizationId
  });

  const handleAddNote = async (_values: INoteFormPayload) => {
    const { title, note, mentions, attachments, category } = _values;

    if (!title || !title.trim()) {
      return;
    }

    const validMentions = mentions
      ?.filter(
        mention =>
          mention?.id && mention?.name && mention?.email && mention?.type
      )

    const response = await createNoteMutation.mutateAsync({
      title,
      content: note,
      category,
      attachments,
      mentions:
        validMentions && validMentions.length > 0 ? validMentions : undefined
    });

    if (response.isSuccess) {
      isResettingRef.current = true;
      reset(
        {
          note: '',
          title: '',
          category: 'General',
          mentions: [],
          attachments: []
        },
        {
          keepDefaultValues: false
        }
      );
      // Re-mount
      setEditorKey(prev => prev + 1);
      setFormKey(prev => prev + 1);
      setTimeout(() => {
        isResettingRef.current = false;
      }, 50);
      onAddNote?.();
      toast({
        variant: 'success',
        title: 'New Note Added'
      });
    } else {
      let errorMsg = response.message;
      // handing zod error from api response
      if (errorMsg?.errors.length) {
        errorMsg = errorMsg.errors?.[0]?.message;
      }
      toast({
        variant: 'error',
        title: errorMsg
      });
    }
  };

  const handleQueryChange = useCallback(
    debounce((query: string, key: '@' | '#') => {
      if (key === '@') return setUserQuery(query);
      return setJournalQuery(query);
    }, 300),
    [setUserQuery, setJournalQuery]
  );

  return (
    <FormProvider {...method}>
      <form onSubmit={handleSubmit(handleAddNote)}>
        <Flex
          key={formKey}
          justifyContent="start"
          gap={4}
          flexDirection="column"
        >
          <Flex flexDirection="column" flex="1" gap={2}>
            <FieldLabel label="Title" subText="" isRequired={true} />
            <Input
              {...register('title')}
              type="text"
              placeholder="Enter the title"
              maxLength={100}
            />
            <Flex flexDirection="column" width="100%">
              <Flex justifyContent="space-between" width="100%">
                <Text textStyle="textContext">
                  Character limit for the title is 100 characters.
                </Text>
                <Text textStyle="textContext">
                  {(watch('title') || '').length} / 100
                </Text>
              </Flex>
              <Flex mt={1}>
                {errors.title && (
                  <FormFeedback variant="error">
                   {toSentenceCasePreserveAcronyms(errors.title?.message ?? '')}
                  </FormFeedback>
                )}
              </Flex>
            </Flex>
          </Flex>
          <Flex flexDirection="column" flex="1" gap={2}>
            <FieldLabel
              label="Note"
              subText="Add notes about this submission and collaborate by tagging team
            members with @ or journal staff with #."
              subTextProps={{ textStyle: 'textContext' }}
              isRequired={false}
            />
            <TaggingEditor
              key={editorKey}
              entities={{
                isLoading: isLoadingUsers || isLoadingJournals,
                at: mappedUsers || [],
                hashtag: mappedJournals || []
              }}
              setQuery={handleQueryChange}
              onContentChange={({ note, mentions }) => {
                if (isResettingRef.current) {
                  return;
                }
                setValue('note', note);
                setValue('mentions', [...mentions]);
              }}
            />
            <Flex justifyContent="space-between">
              <Text textStyle="textContext">
                Character limit for note is 1000 characters.
              </Text>

              <Text textStyle="textContext">
                {(watch('note') || '').length} / 1000
              </Text>
            </Flex>
            {(errors.note || errors.mentions) && (
              <FormFeedback variant="error">
                {errors.note?.message || errors.mentions?.message}
              </FormFeedback>
            )}
          </Flex>
          <Flex flexDirection="column" flex="1" gap={2}>
            <FieldLabel subText="" label="Category" isRequired={false} />
            <Select {...register('category')} maxWidth="240px">
              <option value="General">General</option>
              <option value="Permission">Permission</option>
            </Select>
          </Flex>
          <FormFieldMultipleFileUpload
            submissionContextId={ctxId}
            submissionId={submissionId}
            config={{
              name: 'attachments',
              params: {
                validations: {
                  kind: 'fileArray',
                  maxSizeMB: 25,
                  maxItems: 15,
                  allowedFileFormats: {
                    'image/jpeg': ['.jpg'],
                    'image/png': ['.png'],
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc'],
                    'application/vnd.openxmlformats': ['.docx'],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx']
                  },
                  required: false
                },
                labels: {
                  main: 'Attach File(s)',
                  subtitle: ''
                }
              },
              caseDataFields: {
                main: 'attachments'
              }
            }}
            helperText="PDF, DOCX, XLSX, PNG and JPG"
          />

          <ProtectedActionButton
            relation="workflow_viewer"
            entityType="submission"
            entityId={submissionId}
            size="context"
            variant={actionButtonProps?.variant ?? 'primary'}
            as="button"
            type="submit"
            width="fit-content"
            iconLeft={actionButtonProps?.iconLeft ?? undefined}
          >
            Submit
          </ProtectedActionButton>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default AddNoteForm;
